import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import Footer from '../../components/layout/Footer';
import { NamedGrayIcon } from '../../components/icons/GrayIconBox';
import '../../styles/design-system/index.css';
import { TEACHER_COUNT_LABEL } from '../../constants/siteStats';

const CommunityLandingPage = () => {
  const navigate = useNavigate();

  const faqData = [
    { q: '교사 커뮤니티는 누구나 가입할 수 있나요?', a: '왓퀴즈 교사 커뮤니티는 교사 인증을 완료한 현직 교사만 참여할 수 있습니다. 익명으로 활동하되, 가입은 교사만 가능하여 신뢰도 높은 소통 환경을 유지합니다.' },
    { q: '익명으로 글을 쓸 수 있나요?', a: '네, 모든 게시글과 댓글은 익명으로 작성됩니다. 교사 인증만 완료되면 닉네임 없이도 솔직한 이야기를 나눌 수 있습니다.' },
    { q: '다른 교사가 만든 수업자료를 볼 수 있나요?', a: '네, 자료 라이브러리에서 다른 교사들이 AI로 만든 PPT, 학습지, 이미지 등의 수업자료를 검색하고 활용할 수 있습니다. 비로그인도 열람 가능합니다.' },
    { q: '내가 만든 자료를 공유하면 보상이 있나요?', a: '자료를 공유하면 커뮤니티 뱃지와 리워드를 받을 수 있습니다. 인기 자료 제작자는 PRO 이용권이 지급됩니다.' },
    { q: '인디스쿨과 뭐가 다른가요?', a: '인디스쿨은 자료 공유와 게시판 중심의 전통적 커뮤니티입니다. 왓퀴즈 커뮤니티는 AI로 만든 고품질 수업자료를 원클릭으로 공유하고, AI 자료 생성 + 커뮤니티 + 교사 업무 자동화가 결합된 올인원 플랫폼입니다.' },
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "name": "왓퀴즈 교사 커뮤니티",
        "description": "교사 전용 익명 커뮤니티. AI 수업자료 공유, 교사 소통, 학급 운영 노하우 교환.",
        "url": "https://whatquiz.co.kr/community",
        "publisher": { "@type": "Organization", "name": "왓퀴즈" }
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqData.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } }))
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>교사 커뮤니티 | 익명 소통 · AI 수업자료 공유 · 교사 자료실 | 왓퀴즈</title>
        <meta name="description" content={`현직 교사 전용 익명 커뮤니티. AI로 만든 PPT, 학습지, 이미지를 원클릭 공유. 교사 소통, 학급 운영 노하우, 수업 아이디어 교환. ${TEACHER_COUNT_LABEL} 교사가 함께하는 왓퀴즈.`} />
        <meta name="keywords" content="교사 커뮤니티, 교사 익명 게시판, 교사 소통 사이트, 교원 커뮤니티, 수업자료 공유, 교사 자료실, 교사 익명, 학급 운영 커뮤니티, 교사 고민 상담, 교사 자료 나눔" />
        <link rel="canonical" href="https://whatquiz.co.kr/community" />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://whatquiz.co.kr/community" />
        <meta property="og:title" content="교사 커뮤니티 | 익명 소통 · AI 자료 공유 | 왓퀴즈" />
        <meta property="og:description" content={`현직 교사 전용 익명 커뮤니티. AI 수업자료 원클릭 공유. ${TEACHER_COUNT_LABEL} 교사와 함께.`} />
        <meta property="og:image" content="https://whatquiz.co.kr/og-image-whatquiz-2025.png" />
        <meta property="og:locale" content="ko_KR" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>

      <div style={{ fontFamily: 'var(--whatquiz-font-family)', backgroundColor: '#fff', minHeight: '100vh' }}>
        {/* 히어로 */}
        <section style={{ padding: '100px 20px 80px', textAlign: 'center', background: 'linear-gradient(180deg, #e8f3ff 0%, #fff 100%)' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <span style={{ display: 'inline-block', padding: '6px 16px', backgroundColor: '#dbeafe', color: '#1e40af', borderRadius: '20px', fontSize: '14px', fontWeight: '600', marginBottom: '16px' }}>
              교사 전용
            </span>
            <h1 style={{ fontSize: 'clamp(28px, 5vw, 44px)', fontWeight: '800', color: '#111', lineHeight: '1.3', marginBottom: '16px' }}>
              선생님들의<br />솔직한 이야기가 모이는 곳
            </h1>
            <p style={{ fontSize: '18px', color: '#64748b', lineHeight: '1.7', marginBottom: '32px' }}>
              왓퀴즈 교사 커뮤니티는 현직 교사만 참여할 수 있는 익명 소통 공간입니다.
              수업 고민, 학급 운영 노하우, AI로 만든 수업자료까지 자유롭게 나눠보세요.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => navigate('/community')} style={{ minHeight: '48px', padding: '14px 32px', background: '#3182f6', color: '#fff', fontSize: '16px', fontWeight: '700', border: 'none', borderRadius: '12px', cursor: 'pointer' }}>
                커뮤니티 참여하기
              </button>
              <button onClick={() => navigate('/library')} style={{ padding: '16px 32px', background: '#fff', color: '#334155', fontSize: '16px', fontWeight: '600', border: '1px solid #e2e8f0', borderRadius: '12px', cursor: 'pointer' }}>
                자료 라이브러리 둘러보기
              </button>
            </div>
          </div>
        </section>

        {/* 3가지 핵심 가치 */}
        <section style={{ maxWidth: '960px', margin: '0 auto', padding: '60px 20px' }}>
          <h2 style={{ fontSize: '28px', fontWeight: '700', color: '#111', textAlign: 'center', marginBottom: '48px' }}>왓퀴즈 커뮤니티가 특별한 이유</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px' }}>
            {[
              { icon: 'lock', title: '완전한 익명', desc: '교사 인증만 완료하면 닉네임 없이 자유롭게. 학교 눈치 없이 솔직한 이야기를 나누세요. 블라인드처럼, 교사만을 위해.' },
              { icon: 'spark', title: 'AI 자료 자동 저장 & 공유', desc: 'AI로 만든 PPT, 학습지, 이미지가 라이브러리에 자동 저장. 나눔터에서 다른 선생님들과 간편하게 공유할 수 있어요.' },
              { icon: 'users', title: '교사만의 공간', desc: '현직 교사 인증 필수. 학부모, 학생 걱정 없이 교사끼리만 소통합니다. 수업 고민부터 학교 이야기까지.' },
            ].map((item, idx) => (
              <div key={idx} style={{ background: '#f8f9fa', border: 'none', borderRadius: '16px', padding: '32px 24px', textAlign: 'center' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}><NamedGrayIcon name={item.icon} /></div>
                <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111', marginBottom: '8px' }}>{item.title}</h3>
                <p style={{ fontSize: '14px', color: '#64748b', lineHeight: '1.7' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Community Flywheel */}
        <section style={{ padding: '60px 20px', background: '#fff' }}>
          <div style={{ maxWidth: '720px', margin: '0 auto', textAlign: 'center' }}>
            <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111', marginBottom: '16px' }}>교사가 만들고, 교사가 나누는 선순환</h2>
            <p style={{ fontSize: '16px', color: '#64748b', lineHeight: '1.8', marginBottom: '32px' }}>
              AI로 수업자료를 만들면 라이브러리에 자동 저장 → 나눔터에 공유 → 다른 교사가 활용 → 더 많은 교사가 참여하는 선순환 구조입니다.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '8px' }}>
              {['AI로 PPT 만들기', '→', '라이브러리 자동 저장', '→', '나눔터에 공유', '→', '다른 교사 활용', '→', '더 많은 참여'].map((step, idx) => (
                <span key={idx} style={{ padding: step === '→' ? '8px 4px' : '8px 16px', background: step === '→' ? 'transparent' : '#f8f9fa', border: 'none', borderRadius: '20px', fontSize: '13px', fontWeight: step === '→' ? '400' : '600', color: step === '→' ? '#94a3b8' : '#334155' }}>
                  {step}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* 경쟁사 비교 */}
        <section style={{ maxWidth: '720px', margin: '0 auto', padding: '60px 20px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111', textAlign: 'center', marginBottom: '32px' }}>교사 커뮤니티 비교</h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr style={{ background: '#f1f5f9' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700' }}>비교 항목</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700' }}>인디스쿨</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700' }}>참쌤스쿨</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '700', color: '#3182f6' }}>왓퀴즈</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['대상', '초등교사', '초등교사', '초·중·고 전체'],
                  ['자료 품질', '교사 직접 제작', '디자인 전문', 'AI 자동 생성'],
                  ['자료 유형', '한글, PPT 파일', 'PPT 템플릿', 'PPT + 학습지 + 이미지'],
                  ['AI 기능', '없음', '없음', '생기부 + PPT + 학습지 + 채점'],
                  ['익명 소통', '닉네임 기반', '없음', '완전 익명'],
                  ['가격', '무료', '무료/유료', '무료로 시작'],
                ].map(([category, a, b, c], idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '12px 16px', fontWeight: '600' }}>{category}</td>
                    <td style={{ padding: '12px 16px' }}>{a}</td>
                    <td style={{ padding: '12px 16px' }}>{b}</td>
                    <td style={{ padding: '12px 16px', fontWeight: '600', color: '#3182f6' }}>{c}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* FAQ */}
        <section style={{ maxWidth: '720px', margin: '0 auto', padding: '60px 20px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#111', textAlign: 'center', marginBottom: '32px' }}>자주 묻는 질문</h2>
          {faqData.map((faq, idx) => (
            <div key={idx} style={{ borderBottom: '1px solid #e2e8f0', padding: '20px 0' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#111', marginBottom: '8px' }}>{faq.q}</h3>
              <p style={{ fontSize: '15px', color: '#64748b', lineHeight: '1.7' }}>{faq.a}</p>
            </div>
          ))}
        </section>

        {/* CTA */}
        <section style={{ padding: '60px 20px', textAlign: 'center', background: 'linear-gradient(180deg, #e8f3ff 0%, #fff 100%)' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#111', marginBottom: '12px' }}>{TEACHER_COUNT_LABEL} 교사와 함께하세요</h2>
          <p style={{ fontSize: '16px', color: '#64748b', marginBottom: '24px' }}>가입만 하면 무료로 참여할 수 있습니다</p>
          <button onClick={() => navigate('/community')} style={{ minHeight: '48px', padding: '14px 32px', background: '#3182f6', color: '#fff', fontSize: '16px', fontWeight: '700', border: 'none', borderRadius: '12px', cursor: 'pointer' }}>
            커뮤니티 참여하기
          </button>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default CommunityLandingPage;
